import { ChevronLeft } from 'lucide-react';
import { ButtonBase, Link } from '@/shared/ui';

import { spacing } from '@/config/ui';
import type { RestrictedAccessPageContent } from '@/entities/auth-access';
import Button from '@/shared/ui/primitives/Button';
import { SupportContact } from '@/shared/ui/primitives/SupportContact';
import Typography from '@/shared/ui/typography/Text';

type RestrictedAccessContentProps = {
  content: RestrictedAccessPageContent;
  onPrimaryActionClick: () => void;
};

export const RestrictedAccessContent = ({
  content,
  onPrimaryActionClick
}: RestrictedAccessContentProps) => (
  <>
    <Typography
      size={32}
      sx={{
        fontWeight: 700,
        lineHeight: 1.15,
        mb: spacing[4]
      }}
    >
      {content.title}
    </Typography>

    <Typography
      size={20}
      sx={{
        color: 'gx.subtext',
        mb: spacing[4]
      }}
    >
      {content.subtitle}
    </Typography>

    <Button
      fullWidth
      size='large'
      sx={{
        width: '100%',
        maxWidth: '480px',
        mb: spacing[4]
      }}
      variant='contained'
      onClick={onPrimaryActionClick}
    >
      {content.primaryActionLabel}
    </Button>

    {content.secondaryLinkLabel && content.secondaryLinkHref && (
      <Link
        className='mb-10 inline-flex text-gx-subtext no-underline underline-offset-[3px] hover:underline'
        href={content.secondaryLinkHref}
        rel='noopener noreferrer'
        target='_blank'
      >
        {content.secondaryLinkLabel}
      </Link>
    )}

    <SupportContact sx={{ color: 'gx.subtext', fontSize: '1rem' }} />
  </>
);

type RestrictedAccessPageProps = {
  content: RestrictedAccessPageContent;
  onBackClick: () => void;
  onPrimaryActionClick: () => void;
};

const RestrictedAccessPage = ({
  content,
  onBackClick,
  onPrimaryActionClick
}: RestrictedAccessPageProps) => (
  <div className='mx-auto mt-2 flex w-full max-w-[720px] flex-col items-start md:mt-4'>
    <ButtonBase
      sx={{
        color: 'common.white',
        mb: spacing[6]
      }}
      onClick={onBackClick}
    >
      <ChevronLeft className='mr-2 size-4' />
      <Typography
        size={16}
        sx={{ fontWeight: 500 }}
      >
        {content.backLabel}
      </Typography>
    </ButtonBase>

    <RestrictedAccessContent
      content={content}
      onPrimaryActionClick={onPrimaryActionClick}
    />
  </div>
);

export default RestrictedAccessPage;
